// src/store/slices/jewellerySlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  getDashboardStatsThunk,
  getDailyCollectionsThunk,
  getOverduesThunk,
  getMaturitiesThunk,
  exportLedgerThunk,
  createRedemptionThunk,
  getAllRedemptionsThunk,
  getRedemptionByIdThunk,
  getCustomerRedemptionsThunk,
  approveRedemptionThunk,
  cancelRedemptionThunk,
} from "../../thunks/jewelleryPlan/jewelleryThunks";

const initialState = {
  dashboard: {
    stats: {
      activePlans: 0,
      newPlans: 0,
      totalCollections: 0,
      pendingVerifications: 0,
      upcomingMaturities: 0,
      recentRedemptions: 0,
      period: "month",
    },
    recentActivity: [],
    notifications: [],
    loading: false,
  },
  rates: {
    current: [],
    history: [],
    lastUpdated: null,
    loading: false,
  },
  reports: {
    dailyCollections: null,
    overdues: null,
    maturities: null,
    loading: false,
  },
  redemptions: {
    list: [],
    selectedRedemption: null,
    customerRedemptions: [],
    loading: false,
    pagination: null,
  },
  selectedBranch: null,
  error: null,
};

const jewellerySlice = createSlice({
  name: "jewellery",
  initialState,
  reducers: {
    setSelectedBranch: (state, action) => {
      state.selectedBranch = action.payload;
    },
    clearJewelleryError: (state) => {
      state.error = null;
    },
    updateNotifications: (state, action) => {
      state.dashboard.notifications = action.payload;
    },
    clearSelectedRedemption: (state) => {
      state.redemptions.selectedRedemption = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard Stats
      .addCase(getDashboardStatsThunk.pending, (state) => {
        state.dashboard.loading = true;
      })
      .addCase(getDashboardStatsThunk.fulfilled, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.stats = action.payload.data.stats;
        state.dashboard.stats.period = action.payload.data.period;
      })
      .addCase(getDashboardStatsThunk.rejected, (state, action) => {
        state.dashboard.loading = false;
        state.error = action.payload;
      })

      // Daily Collections
      .addCase(getDailyCollectionsThunk.pending, (state) => {
        state.reports.loading = true;
      })
      .addCase(getDailyCollectionsThunk.fulfilled, (state, action) => {
        state.reports.loading = false;
        state.reports.dailyCollections = action.payload.data;
      })
      .addCase(getDailyCollectionsThunk.rejected, (state, action) => {
        state.reports.loading = false;
        state.error = action.payload;
      })

      // Overdues
      .addCase(getOverduesThunk.pending, (state) => {
        state.reports.loading = true;
      })
      .addCase(getOverduesThunk.fulfilled, (state, action) => {
        state.reports.loading = false;
        state.reports.overdues = action.payload.data;
      })
      .addCase(getOverduesThunk.rejected, (state, action) => {
        state.reports.loading = false;
        state.error = action.payload;
      })

      // Maturities
      .addCase(getMaturitiesThunk.pending, (state) => {
        state.reports.loading = true;
      })
      .addCase(getMaturitiesThunk.fulfilled, (state, action) => {
        state.reports.loading = false;
        state.reports.maturities = action.payload.data;
      })
      .addCase(getMaturitiesThunk.rejected, (state, action) => {
        state.reports.loading = false;
        state.error = action.payload;
      })

      // Export Ledger
      .addCase(exportLedgerThunk.pending, (state) => {
        state.reports.loading = true;
      })
      .addCase(exportLedgerThunk.fulfilled, (state) => {
        state.reports.loading = false;
      })
      .addCase(exportLedgerThunk.rejected, (state, action) => {
        state.reports.loading = false;
        state.error = action.payload;
      })

      // Create Redemption
      .addCase(createRedemptionThunk.pending, (state) => {
        state.redemptions.loading = true;
      })
      .addCase(createRedemptionThunk.fulfilled, (state, action) => {
        state.redemptions.loading = false;
        state.redemptions.list.unshift(action.payload.data);
      })
      .addCase(createRedemptionThunk.rejected, (state, action) => {
        state.redemptions.loading = false;
        state.error = action.payload;
      })

      // Get All Redemptions
      .addCase(getAllRedemptionsThunk.pending, (state) => {
        state.redemptions.loading = true;
      })
      .addCase(getAllRedemptionsThunk.fulfilled, (state, action) => {
        state.redemptions.loading = false;
        state.redemptions.list = action.payload.data;
        state.redemptions.pagination = action.payload.pagination;
      })
      .addCase(getAllRedemptionsThunk.rejected, (state, action) => {
        state.redemptions.loading = false;
        state.error = action.payload;
      })

      // Get Redemption By ID
      .addCase(getRedemptionByIdThunk.pending, (state) => {
        state.redemptions.loading = true;
      })
      .addCase(getRedemptionByIdThunk.fulfilled, (state, action) => {
        state.redemptions.loading = false;
        state.redemptions.selectedRedemption = action.payload.data;
      })
      .addCase(getRedemptionByIdThunk.rejected, (state, action) => {
        state.redemptions.loading = false;
        state.error = action.payload;
      })

      // Get Customer Redemptions
      .addCase(getCustomerRedemptionsThunk.pending, (state) => {
        state.redemptions.loading = true;
      })
      .addCase(getCustomerRedemptionsThunk.fulfilled, (state, action) => {
        state.redemptions.loading = false;
        state.redemptions.customerRedemptions = action.payload.data;
      })
      .addCase(getCustomerRedemptionsThunk.rejected, (state, action) => {
        state.redemptions.loading = false;
        state.error = action.payload;
      })

      // Approve Redemption
      .addCase(approveRedemptionThunk.pending, (state) => {
        state.redemptions.loading = true;
      })
      .addCase(approveRedemptionThunk.fulfilled, (state, action) => {
        state.redemptions.loading = false;
        const index = state.redemptions.list.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions.list[index] = action.payload.data;
        }
        if (
          state.redemptions.selectedRedemption?._id === action.payload.data._id
        ) {
          state.redemptions.selectedRedemption = action.payload.data;
        }
      })
      .addCase(approveRedemptionThunk.rejected, (state, action) => {
        state.redemptions.loading = false;
        state.error = action.payload;
      })

      // Cancel Redemption
      .addCase(cancelRedemptionThunk.pending, (state) => {
        state.redemptions.loading = true;
      })
      .addCase(cancelRedemptionThunk.fulfilled, (state, action) => {
        state.redemptions.loading = false;
        const index = state.redemptions.list.findIndex(
          (r) => r._id === action.payload.data._id
        );
        if (index !== -1) {
          state.redemptions.list[index] = action.payload.data;
        }
        if (
          state.redemptions.selectedRedemption?._id === action.payload.data._id
        ) {
          state.redemptions.selectedRedemption = action.payload.data;
        }
      })
      .addCase(cancelRedemptionThunk.rejected, (state, action) => {
        state.redemptions.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedBranch,
  clearJewelleryError,
  updateNotifications,
  clearSelectedRedemption,
} = jewellerySlice.actions;

export default jewellerySlice.reducer;
